import { type MouseEvent } from 'react'
import { useJustLaunchedTag } from 'experiments/pen-5038_v1_mega_content_picker'
import {
  AnimationHandler,
  Badge,
  Nameplate,
  TileCaption,
  TileImage,
  TileOverlay,
  HoverHandler,
  Button,
  IconPlayDefault,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { SeriesLogo } from 'components/SeriesLogo'
import { getIsSeries } from 'libs/utils'
import { CourseSubType } from 'types/data'
import { useVideoContext } from '../VideoContext'
import { trackWatchTrailerClick } from '../events'
import { ClassTileWrapper } from './ClassTileWrapper'
import { ClassTileProps } from './types'
import {
  tileOptimizedImage,
  nameplateOptimizedImage,
  getDuration,
} from './utils'

// This extends the `img` attributes TS interface to allow use of a `fetchpriority` attribute
// The types support `fetchPriority` (camelCase) but there's a bug in React that raises an error with this naming
// It's not clear to me what combination of React/Next versions actually fix this issue
// https://github.com/vercel/next.js/issues/65161
declare module 'react' {
  interface HTMLAttributes<T>
    extends React.AriaAttributes,
      React.DOMAttributes<T> {
    fetchpriority?: 'high' | 'low' | 'auto'
  }
}

export const ClassTile = ({
  tile,
  carouselSlug,
  location,
  onClick,
  openNewPage = false,
  onClickWatchTrailer,
  isPrioritized = false,
  // pen-5038_v1 experiment
  isJustLaunched = false,
}: ClassTileProps) => {
  const { t } = useTranslation('@mc/next')
  const { openVideo } = useVideoContext()

  const handleLinkClick = () => {
    onClick({
      classTitle: tile.title,
      carouselSlug,
      location,
      link: tile.link,
    })
  }

  const handleClickWatchTrailer = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!tile.mediaUuid) {
      return
    }

    trackWatchTrailerClick({
      location,
      slug: tile.slug,
      type: tile.type as CourseSubType,
    })

    if (onClickWatchTrailer) {
      onClickWatchTrailer()
    } else {
      openVideo?.(tile)
    }
  }

  const isSeries = getIsSeries(tile.type)

  const originalTag = tile.releaseDescription ?? tile.tag
  const tag = useJustLaunchedTag({
    isJustLaunched,
    originalTag,
  })

  return (
    <HoverHandler>
      {({ hovering: isHovering, focused }) => (
        <ClassTileWrapper
          clickBehavior='navigate'
          href={tile.link}
          openNewPage={openNewPage}
          onLinkClick={handleLinkClick}
          onWatchTrailerClick={handleClickWatchTrailer}
        >
          {tag && (
            <TileCaption position='left top' padding={4}>
              <Badge>
                <span className='mc-text--capitalize'>{tag}</span>
              </Badge>
            </TileCaption>
          )}

          <TileOverlay />

          <AnimationHandler type='zoom' animating={isHovering || focused}>
            <TileImage
              image={
                // explicitly use a standard `img` element so we can deterministically preload images
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  alt=''
                  src={tileOptimizedImage(tile.primary2x3)}
                  fetchpriority={isPrioritized ? 'high' : 'auto'} // eslint-disable-line react/no-unknown-property
                  loading={isPrioritized ? undefined : 'lazy'}
                />
              }
            />
          </AnimationHandler>

          <TileCaption padding={4} position='center bottom' flush>
            {isSeries && <SeriesLogo width={137} />}

            <Nameplate
              size='small'
              image={
                // explicitly use a standard `img` element so we can deterministically preload images
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  alt={
                    isSeries ? tile.title : tile.instructorName ?? tile.title
                  }
                  src={nameplateOptimizedImage(tile.nameplateImage)}
                  style={{ width: '100%' }}
                  fetchpriority={isPrioritized ? 'high' : 'auto'} // eslint-disable-line react/no-unknown-property
                  loading={isPrioritized ? undefined : 'lazy'}
                />
              }
              separator={!isSeries}
              subText={!isSeries ? tile.headline : undefined}
            />

            {tile?.mediaUuid ? (
              <Button
                onClick={handleClickWatchTrailer}
                kind='tertiary'
                fullWidth
                className='mc-mt-3 mc-px-3'
              >
                <IconPlayDefault size='sm' className='mc-mr-2' />
                {t('home.trendingCarousel.watchTrailer')}
              </Button>
            ) : (
              <span className='mc-mt-3 mc-text-x-small'>
                {getDuration(tile.link, tile.duration)}
              </span>
            )}
          </TileCaption>
        </ClassTileWrapper>
      )}
    </HoverHandler>
  )
}
